/**
 * Conditions — the block the Create-rule modal and the Ads Manager filter
 * share: the card with "+ Add condition", the condition row (metric ·
 * operator · value · ×) and the AND/OR picker between rows. One
 * implementation on purpose (TZ_Code_AdsManager_Filter §0): the rule-only
 * parts (cohort window, "after that rule last time applied") are switched
 * off by props, not copied.
 */

import { useEffect } from "react";
import {
  type RuleConditionV2,
  type EventMetricKindV2,
} from "@/lib/api-hooks";
import {
  useCustomMetricReadiness,
  type CustomMetric,
} from "@/lib/ltv-hooks";

export const METRICS = [
  "Impressions", "Taps", "TTR", "Average CPT", "Spend",
  "Installs (Tap)", "Installs (View)", "Installs (Total)",
  "CR (Tap)", "CR (Total)",
  "Average CPA (Tap)", "Average CPA (View)", "Average CPA (Total)",
  "New downloads (Tap)", "New downloads (View)", "New downloads (Total)",
] as const;

// Only meaningful at keyword / ad_group / search_term grain: SOV matches the
// entity's own search term (single-country campaigns), IPM mirrors the
// dashboard column. Values must equal the backend DSL names exactly.
/** Читаемая подпись сохранённой метрики, для которой опции нет:
 * `event_cpa:MobilePurchase` → «MobilePurchase · CPA ($)», плюс «(loading…)»
 * пока список ещё в пути. Список пришёл, а опции всё нет — событие у
 * организации больше не встречается; значение показываем как есть. */
function describeSavedMetric(metric: string, loading: boolean): string {
  const m = /^([a-z_]+:)(.+)$/.exec(metric);
  const base = m
    ? `${m[2]}${m[1] === "event_cpa:" ? " · CPA ($)" : m[1] === "event_cr:" ? " · CR (%)" : ""}`
    : metric;
  return loading ? `${base} (loading…)` : base;
}

export const KW_AG_METRICS = [
  { value: "IPM", label: "IPM (installs / 1000 impr.)" },
  { value: "Share of Voice", label: "Share of Voice — slot 1 (%)" },
  { value: "SOV slot 2", label: "SOV slot 2 (%)" },
  { value: "SOV total", label: "SOV total (%)" },
] as const;
export const KW_AG_METRIC_LEVELS = new Set(["keyword", "ad_group", "search_term"]);

export const OPS = [
  { id: "greater than", label: "greater than", glyph: ">" },
  { id: "less than", label: "less than", glyph: "<" },
  { id: "equals", label: "equals", glyph: "=" },
  { id: "not equals", label: "not equals", glyph: "≠" },
  { id: "in range", label: "in range", glyph: "↔" },
  { id: "not in range", label: "not in range", glyph: "⇄" },
] as const;
export const LB_UNITS = ["hours", "days", "weeks", "months"] as const;

/** AND/OR between a condition and the one above it — one component for the
 *  rule modal and the Ads Manager filter, so "unpicked" looks the same in
 *  both: nothing selected, orange outline, "pick AND or OR to save". The
 *  engine groups runs of AND and ORs the groups (AND binds tighter). */
export function CombinatorPicker({
  value,
  onChange,
}: {
  value: "and" | "or" | null;
  onChange: (op: "and" | "or") => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        margin: "2px 0 2px 2px",
      }}
    >
      {(["and", "or"] as const).map((op) => {
        const active = value === op;
        return (
          <button
            key={op}
            type="button"
            onClick={() => onChange(op)}
            style={{
              padding: "3px 14px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              cursor: "pointer",
              border: active
                ? "1px solid var(--c-accent-600)"
                : value == null
                  ? "1px solid #e07a1f"
                  : "1px solid var(--c-divider)",
              background: active ? "var(--c-accent-600)" : "transparent",
              color: active ? "#fff" : "var(--c-text-2)",
            }}
          >
            {op}
          </button>
        );
      })}
      {value == null && (
        <span style={{ fontSize: 12, color: "#e07a1f" }}>
          pick AND or OR to save
        </span>
      )}
    </div>
  );
}

export function ConditionsCard({
  title,
  borderColor,
  background,
  onAdd,
  addLabel,
  titleExtra,
  className,
  children,
}: {
  title: string;
  borderColor: string;
  background: string;
  onAdd?: () => void;
  addLabel?: string;
  /** Next to the title — the filter puts its "Clear" here. */
  titleExtra?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={className}
      style={{
        background,
        border: "1px solid var(--c-divider)",
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{ fontSize: 13, fontWeight: 600, color: "var(--c-text)" }}
          >
            {title}
          </div>
          {titleExtra}
        </div>
        {onAdd && addLabel && (
          <button
            onClick={onAdd}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--c-accent-600)",
              font: "inherit",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: 6,
            }}
          >
            {addLabel}
          </button>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

export function ConditionRow({
  value,
  index = 0,
  onChange,
  onDelete,
  canDelete,
  showActionBased,
  actionBasedApplies,
  actionBased,
  onActionBasedChange,
  eventTypes,
  eventMetricKinds,
  customMetrics,
  appId,
  onReadiness,
  level,
  ops = OPS,
  showWindow = true,
  excludeMetrics,
}: {
  value: RuleConditionV2;
  index?: number;
  onChange: (v: RuleConditionV2) => void;
  onDelete: () => void;
  canDelete: boolean;
  /** Рисовать переключатель «after that rule last time applied» (только
   *  первая строка круга 2). */
  showActionBased?: boolean;
  /** Строка живёт в круге 2: при включённом переключателе окно у неё
   *  берётся с последнего применения — поле периода не показывается. Движок
   *  применяет action-based когорту к КАЖДОМУ условию круга 2, а не только к
   *  первому; до этого поле «for the last N days» показывалось на всех
   *  строках, кроме первой, и читалось как обязательное. */
  actionBasedApplies?: boolean;
  actionBased?: boolean;
  onActionBasedChange?: () => void;
  eventTypes?: readonly { value: string; label: string; category: string | null }[];
  eventMetricKinds?: readonly EventMetricKindV2[];
  customMetrics?: CustomMetric[];
  appId?: number | null;
  onReadiness?: (metricKey: string, ready: boolean) => void;
  level?: string;
  /** Operators to offer. The rule modal takes all six; the Ads Manager
   *  filter — the same row, the same component — only gt / lt / eq / ne. */
  ops?: readonly { id: string; label: string; glyph: string }[];
  /** "for the last N days" belongs to a rule (its cohort window); a table
   *  filter reads the date picker's period instead and hides it. */
  showWindow?: boolean;
  /** Metrics that exist in the vocabulary but can't be evaluated by this
   *  caller (the filter can't read Share of Voice off a table row). */
  excludeMetrics?: ReadonlySet<string>;
}) {
  const v = value;
  const set = (patch: Partial<RuleConditionV2>) => onChange({ ...v, ...patch });
  const inRange = v.op === "in range" || v.op === "not in range";
  const useActionBased = !!actionBased && (!!showActionBased || !!actionBasedApplies);

  // §10.3 completeness: warn (and let the modal block Save) when the selected
  // custom metric's dependencies aren't filled yet.
  const customKey =
    v.metric && v.metric.startsWith("custom_metric:")
      ? v.metric.slice("custom_metric:".length)
      : null;
  // The rule is app-abstract, so `appId` is usually null — resolve the picked
  // metric's own app from the org-wide list for the readiness check.
  const readinessAppId =
    (customKey
      ? customMetrics?.find((m) => m.metric_key === customKey)?.app_id
      : null) ??
    appId ??
    null;
  const readiness = useCustomMetricReadiness(readinessAppId, customKey);
  const notReady = !!customKey && !!readiness.data && !readiness.data.ready;
  useEffect(() => {
    if (customKey && readiness.data) onReadiness?.(customKey, readiness.data.ready);
  }, [customKey, readiness.data, onReadiness]);

  // Сохранённая метрика, для которой опции ещё нет: список событий /
  // кастомных метрик приходит отдельным запросом, и пока он в пути
  // `<select>` со значением `event:MobilePurchase` рисовал бы пустое
  // «Variable» — условие выглядело потерянным. Держим опцию под сохранённым
  // значением, пока настоящая не появится.
  const hasOption =
    !v.metric ||
    (METRICS as readonly string[]).includes(v.metric) ||
    KW_AG_METRICS.some((m) => m.value === v.metric) ||
    (!!eventTypes &&
      !!eventMetricKinds &&
      eventMetricKinds.some(
        (kind) =>
          v.metric.startsWith(kind.prefix) &&
          eventTypes.some((e) => `${kind.prefix}${e.value}` === v.metric),
      )) ||
    (!!customMetrics &&
      customMetrics.some((m) => `custom_metric:${m.metric_key}` === v.metric));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* AND/OR combinator — sits BETWEEN this condition and the one
          above it (only for the 2nd+ condition). Nothing selected by
          default: the user MUST pick, and Save is blocked until they do
          (empty = orange outline). Engine groups runs of AND together
          and ORs the groups (AND binds tighter). */}
      {index > 0 && (
        <CombinatorPicker
          value={v.combinator ?? null}
          onChange={(op) => set({ combinator: op })}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <select
          className="select"
          value={v.metric}
          onChange={(e) => set({ metric: e.target.value })}
          style={{ flex: "1 1 160px" }}
        >
          <option value="">Variable</option>
          {!hasOption && (
            <option value={v.metric}>
              {describeSavedMetric(v.metric, !eventTypes || !customMetrics)}
            </option>
          )}
          <optgroup label="ASA metrics">
            {METRICS.filter((m) => !excludeMetrics?.has(m)).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
            {level != null && KW_AG_METRIC_LEVELS.has(level) &&
              KW_AG_METRICS.filter((m) => !excludeMetrics?.has(m.value)).map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
          </optgroup>
          {/* One group per quantity the engine can build on an event —
              count / CPA / CR. Both the prefix and the levels it's valid
              on come from the backend (dsl.EVENT_METRIC_KINDS), so this
              dropdown cannot offer a metric the engine has no branch for.
              Don't hardcode a prefix here. */}
          {eventTypes && eventTypes.length > 0 &&
            eventMetricKinds
              ?.filter((kind) => level == null || kind.levels.includes(level))
              .map((kind) => (
                <optgroup key={kind.prefix} label={kind.group_label}>
                  {eventTypes.map((e) => (
                    <option
                      key={`${kind.prefix}${e.value}`}
                      value={`${kind.prefix}${e.value}`}
                    >
                      {e.label}
                      {kind.option_suffix}
                    </option>
                  ))}
                </optgroup>
              ))}
          {customMetrics && customMetrics.length > 0 && (
            <optgroup label="Custom metrics">
              {customMetrics.map((m) => (
                <option
                  key={`${m.app_id ?? "x"}:${m.metric_key}`}
                  value={`custom_metric:${m.metric_key}`}
                >
                  {m.display_name}
                  {m.app_name ? ` · ${m.app_name}` : ""}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <select
          className="select"
          value={v.op}
          onChange={(e) => set({ op: e.target.value })}
          style={{ flex: "1 1 140px" }}
        >
          <option value="">Condition</option>
          {ops.map((o) => (
            <option key={o.id} value={o.id}>{`${o.glyph}  ${o.label}`}</option>
          ))}
        </select>
        {inRange ? (
          <div style={{ display: "flex", gap: 6, flex: "1 1 160px" }}>
            <input
              className="input"
              placeholder="min"
              value={String(v.val ?? "")}
              onChange={(e) => set({ val: e.target.value })}
              style={{ width: "50%" }}
            />
            <span className="muted">–</span>
            <input
              className="input"
              placeholder="max"
              value={String(v.val2 ?? "")}
              onChange={(e) => set({ val2: e.target.value })}
              style={{ width: "50%" }}
            />
          </div>
        ) : (
          <input
            className="input"
            placeholder="0"
            value={String(v.val ?? "")}
            onChange={(e) => set({ val: e.target.value })}
            style={{ flex: "1 1 90px" }}
          />
        )}
        {showWindow && !useActionBased && (
          <>
            <span style={{ fontSize: 13, color: "var(--c-text-3)" }}>
              for the last
            </span>
            <input
              className="input"
              placeholder="0"
              value={String(v.metric_window ?? "")}
              onChange={(e) =>
                set({
                  metric_window: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
              style={{ width: 70 }}
            />
            <select
              className="select"
              value={v.metric_window_unit ?? "days"}
              onChange={(e) =>
                set({
                  metric_window_unit:
                    e.target.value as RuleConditionV2["metric_window_unit"],
                })
              }
              style={{ flex: "0 0 110px" }}
            >
              {LB_UNITS.map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </>
        )}
        <button
          className="btn icon sm"
          onClick={onDelete}
          disabled={!canDelete}
          title="Remove"
        >
          ×
        </button>
      </div>
      {notReady && readiness.data && (
        <div style={{ fontSize: 12, color: "#e07a1f", marginLeft: 2 }}>
          ⚠ Finish setup to use this metric: {readiness.data.missing.join(", ")}
        </div>
      )}
      {showActionBased && (
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 12px",
            borderRadius: 999,
            border: `1.5px solid ${useActionBased ? "#e07a1f" : "#f0a85a"}`,
            background: useActionBased ? "#fff4e6" : "#fffaf3",
            cursor: "pointer",
            alignSelf: "flex-start",
          }}
          onClick={onActionBasedChange}
        >
          <ToggleSwitch on={useActionBased} onChange={onActionBasedChange!} />
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "#a85410",
            }}
          >
            🔥 after that rule last time applied
          </span>
        </label>
      )}
    </div>
  );
}

export function ToggleSwitch({
  on,
  onChange,
}: {
  on: boolean;
  onChange: () => void;
}) {
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      style={{
        width: 32,
        height: 18,
        borderRadius: 999,
        background: on ? "var(--c-accent-600)" : "var(--c-border-strong)",
        position: "relative",
        cursor: "pointer",
        flex: "0 0 32px",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: 2,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,.2)",
          transform: on ? "translateX(14px)" : "none",
          transition: "transform .15s",
        }}
      />
    </span>
  );
}
